/**
 * Jauge du concours d'univers — dans la barre du haut de l'éditeur, pour les seuls participants.
 *
 *   « 🟠 Louise · 3/5 univers · 2,40 $/10 $ »
 *
 * Trois états, sur le plus entamé des deux plafonds (univers, dépense) :
 * - calme : neutre, aux couleurs du texte du thème ;
 * - à 80 % : Citron braise — l'Encre dorée change de teinte selon le thème (cyan en
 *   Ardoise), elle ne dirait pas « attention » partout ;
 * - à 100 % : Rose braise et le message « Limite du jour atteinte — remise à zéro à minuit ».
 *
 * Un non-participant ne voit rien : la route répond `participant: false`, le composant rend
 * null et cesse d'interroger le serveur. Une erreur réseau garde le dernier état connu. Interrogée au montage et toutes les
 * 60 s ; l'intervalle est nettoyé au démontage. Couleurs en `rgba(var(--forge-X), α)` : la
 * polarité du thème clair suit d'elle-même, sans entrée dans la table d'overrides.
 */
import { useEffect, useState } from 'react';
import { lireMonEtatConcours } from '../api/concoursClient';
import { useAuthStore } from '../store/useAuthStore';

const INTERVALLE_MS = 60 * 1000;

function dollars(centimes) {
  const v = centimes / 100;
  return Number.isInteger(v) ? `${v} $` : `${v.toFixed(2).replace('.', ',')} $`;
}

function part(compteur) {
  if (!compteur || compteur.utilises === null || compteur.utilises === undefined || !compteur.plafond) return 0;
  return compteur.utilises / compteur.plafond;
}

export default function JaugeConcours() {
  const userId = useAuthStore((s) => s.user?.id);
  const [etat, setEtat] = useState(null);

  useEffect(() => {
    let annule = false;
    setEtat(null);
    if (!userId) return undefined;
    let minuteur = null;
    const arreter = () => { if (minuteur !== null) { clearInterval(minuteur); minuteur = null; } };
    const lire = async () => {
      try {
        const e = await lireMonEtatConcours();
        if (annule) return;
        setEtat(e);
        // Hors concours : la réponse ne changera pas de la session (relecture de Jabir, #376).
        // Plus aucune requête pour ce compte ; un changement de compte relance l'effet.
        if (e && e.participant === false) arreter();
      } catch (err) {
        // 401 survivant au renouvellement : plus de session, plus de jauge.
        if (!annule && err?.status === 401) setEtat(null);
      }
    };
    minuteur = setInterval(lire, INTERVALLE_MS);
    lire();
    return () => { annule = true; arreter(); };
  }, [userId]);

  if (!etat?.participant) return null;

  const u = etat.univers || {};
  const d = etat.depense_centimes || {};
  const r = etat.reecritures || {};
  const inconnu = u.utilises === null || u.utilises === undefined;
  const taux = Math.max(part(u), part(d));
  const niveau = taux >= 1 ? 'alerte' : taux >= 0.8 ? 'attention' : 'calme';

  // Calme : teinte du texte du thème, discrète. Attention et alerte : pastille PLEINE, encre
  // charbon. Un texte citron ou rose sur fond teinté tombait sous 3:1 sur le gris du thème par
  // défaut (Classic Forge) et sur le beige des thèmes clairs — vérifié en rendu, trois thèmes.
  const ENCRE_CHARBON = '24, 22, 20';
  const style = niveau === 'calme'
    ? {
        color: 'rgba(var(--forge-text), 0.75)',
        backgroundColor: 'rgba(var(--forge-text), 0.06)',
        borderColor: 'rgba(var(--forge-text), 0.12)',
      }
    : {
        color: `rgba(${ENCRE_CHARBON}, 1)`,
        backgroundColor: `rgba(var(${niveau === 'alerte' ? '--forge-rose' : '--forge-lemon'}), 1)`,
        borderColor: `rgba(var(${niveau === 'alerte' ? '--forge-rose' : '--forge-lemon'}), 1)`,
      };

  const texteUnivers = inconnu ? `—/${u.plafond} univers` : `${u.utilises}/${u.plafond} univers`;
  const texteDepense = inconnu ? `—/${dollars(d.plafond)}` : `${dollars(d.utilises)}/${dollars(d.plafond)}`;
  const resume = `${etat.nom} · ${texteUnivers} · ${texteDepense}`;
  const remise = etat.remise_a_zero
    ? new Date(etat.remise_a_zero).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', hour: '2-digit', minute: '2-digit' })
    : 'minuit';
  const infobulle = [
    `Concours d'univers — ${resume}`,
    inconnu ? 'Compteurs momentanément illisibles.' : `Réécritures de prompt : ${r.utilises}/${r.plafond}`,
    `Remise à zéro : ${remise} (heure de Paris)`,
  ].join('\n');

  return (
    <div
      role="status"
      aria-live="polite"
      title={infobulle}
      style={style}
      className="lf-concours flex shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono whitespace-nowrap transition-colors"
    >
      <span aria-hidden="true">{etat.emoji}</span>
      <span className="lf-concours-nom font-semibold">{etat.nom}</span>
      <span aria-hidden="true" className="opacity-50">·</span>
      <span>{texteUnivers}</span>
      <span aria-hidden="true" className="opacity-50">·</span>
      <span>{texteDepense}</span>
      {niveau === 'alerte' && (
        <span className="lf-concours-alerte ml-1 font-semibold">
          Limite du jour atteinte — remise à zéro à minuit
        </span>
      )}
    </div>
  );
}
